import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { FC, useEffect, useMemo, useState } from 'react';
import { getPageEventName } from 'tracks/tracker';
import { getActivityConfig, getActivityContent } from '@/api/mkt';
import Constants from '@/constants/const';
import { FORBIDDEN_ACTIVITY_PATH } from '@/constants/third';
import { getCookie, setCookie } from '@/utils/cookie';
import { getPathNoLangOnClient } from '@/utils/url';
import useAccount from '@/store/account';
import { shouldShowAdsList } from '@/utils/ftu';
import { getPageEventNameV2 } from '@/utils/common/pageKey';
import { getActivityWidgetSuppressedForFirstOrder } from '@/utils/activity-widget-first-order-suppression-bus';

const ActivityWrapper = dynamic(() => import('@/components/activity/components/center/ActivityWrapper'), {
  ssr: false
});

type Props = {
  data?: Record<string, any>;
};

const ACTIVITY_CHACHE_TIME = Constants.ACTIVITY_CENTER_CACHE_TIME;
const ACTIVITY_COOKIE_KEY = Constants.ACTIVITY_CENTER_CACHE_KEY;
const ACTIVITY_DATA_KEY = Constants.ACTIVITY_CENTER_CACHE_DATA_KEY;

const ActivityWidget: FC<Props> = props => {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<ObjectType | null>(null);
  const { isReady, asPath, query } = useRouter();
  const { userPreOrderLogged } = useAccount();
  const hidePop = query?.hide_activity_pop;

  const key = useMemo(() => {
    if (loading) return '';
    return data?.content_page_key || data?.content_type || '';
  }, [data?.content_page_key, data?.content_type, loading]);

  useEffect(() => {
    if (!userPreOrderLogged) return;
    // 如果展示ftu的store或者zipcode弹框，不展示activity弹框
    if (shouldShowAdsList()) return;
    const timer = window.setTimeout(() => {
      const isForbidden = FORBIDDEN_ACTIVITY_PATH.some(path => {
        return asPath.startsWith(path);
      });
      if (isForbidden || !!hidePop || getActivityWidgetSuppressedForFirstOrder()) return;
      let isInCache = getCookie(ACTIVITY_COOKIE_KEY) === 'true';
      const list = JSON.parse(localStorage?.getItem(ACTIVITY_DATA_KEY) || '[]');
      let pageListPromise: Promise<string[]> = new Promise((resolve, reject) => {
        if (isInCache && !!list && !!list?.length) {
          resolve(list);
          return;
        }
        return getActivityConfig({
          cache: isInCache ? true : 'restore'
        }).then(({ object: pageNames }) => {
          !isInCache &&
            setCookie(ACTIVITY_COOKIE_KEY, 'true', null, {
              maxAge: ACTIVITY_CHACHE_TIME
            });
          localStorage.setItem(ACTIVITY_DATA_KEY, JSON.stringify(pageNames || []));
          resolve(pageNames || []);
        }, reject);
      });
      pageListPromise.then((pageNames = []) => {
        const pathNoLang = getPathNoLangOnClient(window.location.pathname);
        let pageName = getPageEventName(pathNoLang) || getPageEventNameV2(pathNoLang);
        if (pageNames?.includes(pageName)) {
          getActivityContent({
            page: pageName
          }).then(({ object = {} }) => {
            if (object?.popup_exist === true) {
              setData({ pageName, ...object?.popup_info });
              setLoading(false);
            }
          });
        }
      });
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [asPath, userPreOrderLogged, hidePop]);

  if (loading || !data) return null;

  return <ActivityWrapper componentKey={key} data={data} />;
};

export default ActivityWidget;
